import { DownChevron, RightChevron } from '../../assets/icons';
import { mapLegStatus } from '../../lib/bet-status';
import { ResultType } from '../../types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { renderResultCellIcon } from './bet-legs';
import { Block, Text } from '../typography';
import { sportsIconsByResultType } from '.';

type AccordionSGPProps = {
  children: any;
  isSGP: boolean;
  legDesc: string;
  eventName: any;
  competitionSportName: any;
  sgpOdds: string;
  sgpOriginalOdds?: string;
  sgpResult: ResultType;
  gridTemplateColumns: string | undefined;
  paddingHorizontal: string;
  isCompetitionSportVisible: boolean;
};

const AccordionSGP = ({
  isSGP,
  children,
  legDesc,
  eventName,
  competitionSportName,
  sgpOdds,
  sgpOriginalOdds,
  sgpResult,
  gridTemplateColumns,
  paddingHorizontal,
  isCompetitionSportVisible
}: AccordionSGPProps) => {
  const [open, setOpen] = useState(false);

  const { label } = mapLegStatus(sgpResult ? { type: sgpResult } : null);

  return (
    <>
      {isSGP ? (
        <>
          <AccordionHeader
            {...{ gridTemplateColumns }}
            open={open}
            onClick={() => setOpen(!open)}
            as="div"
            paddingHorizontal={paddingHorizontal}>
            <LegDetails paddingHorizontal={paddingHorizontal}>
              {open ? <DownChevron width="1.4rem" /> : <RightChevron width="1.4rem" />}
              {legDesc}
            </LegDetails>
            <HeaderContent>-</HeaderContent>
            <HeaderContent>-</HeaderContent>
            <HeaderContent>{eventName}</HeaderContent>
            {isCompetitionSportVisible && <HeaderContent>{competitionSportName}</HeaderContent>}
            <OddsHeaderContent {...{ sgpOdds, sgpOriginalOdds }} />
            <HeaderContent>{renderResultCellIcon({ label, icon: sportsIconsByResultType[sgpResult] })}</HeaderContent>
          </AccordionHeader>
          {open && (
            <AccoridonItem open={open} data-testid="sgp-bet-legs-open">
              {children}
            </AccoridonItem>
          )}
        </>
      ) : (
        children
      )}
    </>
  );
};

export default AccordionSGP;

const AccoridonItem = styled.div<{ open: boolean }>`
  ${({ open }) =>
    open &&
    `border-bottom: 1px solid #343840;
`};
`;

const AccordionHeader = styled.div<{
  gridTemplateColumns: string | undefined;
  open: boolean;
  paddingHorizontal: string;
}>`
  display: grid;
  grid-template-columns: ${({ gridTemplateColumns }) => gridTemplateColumns || '5fr 7fr 5fr 17fr 17fr 3fr 3fr'};
  min-height: 4.4rem;
  align-items: center;
  ${({ open }) =>
    open &&
    `
  border-top: 1px solid #343840;
  border-bottom: 1px solid #DADADA;
`}
  & > div:last-child {
    display: flex;
    justify-content: flex-end;
    padding-right: ${({ paddingHorizontal }) => paddingHorizontal};
  }
`;

const HeaderContent = styled.div`
  padding: 0.6rem 2.5rem 0.6rem 0;
`;

const OddsHeaderContent = ({ sgpOdds, sgpOriginalOdds }: { sgpOdds: string; sgpOriginalOdds?: string }) => {
  return (
    <HeaderContent>
      <OddsBlock>
        {sgpOriginalOdds && <OriginalOdds>{sgpOriginalOdds}</OriginalOdds>}
        <Text>{sgpOdds}</Text>
      </OddsBlock>
    </HeaderContent>
  );
};

const OddsBlock = styled(Block)`
  > * + * {
    margin-left: 0.5rem;
  }
`;

const OriginalOdds = styled(Text)`
  text-decoration: line-through ${({ theme }) => theme.colors.neutral};
  color: ${({ theme }) => theme.colors.neutral};
`;

const LegDetails = styled.div<{ paddingHorizontal: string }>`
  padding: 0.6rem 2.5rem 0.6rem ${({ paddingHorizontal }) => paddingHorizontal};
  display: flex;
  gap: 1rem;
  align-items: center;
`;
